// Order Status Enums
export enum OrderStatus {
  NEW = 'New',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  DELIVERED = 'Delivered',
  CANCELLED = 'Cancelled'
}

export enum OrderSubStatus {
  IN_PRODUCTION = 'In Process',
  READY_FOR_TRIAL = 'Ready for Trial',
  READY_FOR_DELIVERY = 'Ready for Delivery',
  DELIVERED = 'Delivered'
}

// Garment Types
export enum GarmentType {
  JACKET_VEST = 'Jacket & Vest',
  TROUSER = 'Trouser',
  SHIRT = 'Shirt',
  SHERWANI = 'Sherwani',
  KURTA = 'Kurta',
  KAFTAN = 'Kaftan',
  ALTERATION = 'Alteration'
}

// Main Order Interface
export interface Order {
  id: string
  orderId: string                    // Generated ID (JUM-12/2024-001)
  isDraft: boolean
  customerId: string
  customerName: string
  customerContact: string
  customerEmail: string
  customerAddress: string
  customerCity: string
  customerCountry: string
  deliveryAddress: string
  branchId: string
  branchName?: string
  salesmanId: string
  salesmanName: string
  masterName?: string
  status: OrderStatus
  subStatus?: OrderSubStatus
  invoiceNumber?: string
  trialDate1?: Date
  trialDate2?: Date
  deliveryDate?: Date
  totalAmount?: number
  advanceAmount?: number
  balanceAmount?: number
  notes?: string
  createdAt: Date
  updatedAt: Date
  statusHistory: StatusChange[]
  noteEntries: OrderNoteEntry[]
  garments?: OrderItem[]
}

// Order Item Interface
export interface OrderItem {
  id: string
  orderId: string
  garmentType: GarmentType
  subGarmentType?: string
  quantity: number
  description?: string
  remarks?: string
  customerProvidedFabric: boolean
  fabricBrand?: string
  fabricCode?: string
  fabricQuantity?: string
  measurementId: string
  measurementData: MeasurementData
  itemAmount?: number
}

export interface OrderNoteEntry {
  id: string
  orderId: string
  note: string
  createdByEmployeeId?: string
  createdByName: string
  createdAt: Date
  updatedAt: Date
}

// Status Change Interface
export interface StatusChange {
  id: string
  orderId: string
  fromStatus: OrderStatus
  toStatus: OrderStatus
  subStatus?: OrderSubStatus
  changedBy: string
  changedAt: Date
  notes?: string
}

// Order Creation Request
export interface CreateOrderRequest {
  customerId: string
  branchId: string
  employeeId?: string
  salesmanName: string
  deliveryAddress: string
  garmentItems: CreateOrderItemRequest[]
  invoiceNumber?: string
  masterName?: string
  trialDate1?: Date
  trialDate2?: Date
  deliveryDate: Date
  notes?: string
}

export interface CreateOrderDraftRequest {
  customerId: string
  branchId: string
  employeeId?: string
  salesmanName: string
  deliveryAddress?: string
}

export interface SaveDraftGarmentsRequest {
  orderId: string
  garmentItems: CreateOrderItemRequest[]
}

export interface FinalizeDraftOrderRequest {
  id: string
  masterName: string
  invoiceNumber: string
  deliveryAddress: string
  trialDate1?: Date
  trialDate2?: Date
  deliveryDate: Date
  notes?: string
}

// Order Item Creation Request
export interface CreateOrderItemRequest {
  garmentType: GarmentType
  subGarmentType?: string
  quantity: number
  description?: string
  remarks?: string
  customerProvidedFabric: boolean
  fabricBrand?: string
  fabricCode?: string
  fabricQuantity?: string
  measurementId: string
}

// Order Update Request
export interface UpdateOrderRequest {
  id: string
  branchId?: string
  employeeId?: string
  salesmanName?: string
  masterName?: string
  deliveryAddress?: string
  garmentItems?: UpdateOrderItemRequest[]
  invoiceNumber?: string
  trialDate1?: Date
  trialDate2?: Date
  deliveryDate?: Date
  status?: OrderStatus
  subStatus?: OrderSubStatus
  totalAmount?: number
  advanceAmount?: number
  notes?: string
}

// Order Item Update Request
export interface UpdateOrderItemRequest {
  id?: string
  garmentType: GarmentType
  subGarmentType?: string
  quantity: number
  remarks?: string
  customerProvidedFabric: boolean
  fabricBrand?: string
  fabricCode?: string
  fabricQuantity?: string
  measurementId: string
}

// Order Search Result
export interface OrderSearchResult {
  id: string
  orderId: string
  customerName: string
  status: OrderStatus
  createdAt: Date
  deliveryDate?: Date
}

// Global Search Result
export interface GlobalSearchResult {
  type: 'customer' | 'order' | 'employee'
  id: string
  displayName: string
  contact?: string
  email?: string
  status?: OrderStatus
}

// Measurement Data Interface (placeholder - should match measurement module)
export interface MeasurementData {
  id: string
  customerId: string
  garmentType: GarmentType
  measurements: Record<string, number>
  createdAt: Date
  updatedAt: Date
}

// Order ID Generation
export interface OrderIdGenerator {
  generateOrderId(branchCode: string): Promise<string>
  getNextSequence(branchCode: string, monthYear: string): Promise<number>
  validateUniqueOrderId(orderId: string): Promise<boolean>
}

// Order Validation Result
export interface OrderValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
  missingMeasurements: GarmentType[]
}

// Order Statistics
export interface OrderStatistics {
  totalOrders: number
  newOrders: number
  inProgressOrders: number
  readyForTrialOrders: number
  completedOrders: number
  deliveredOrders: number
}

// Order Filter Options
export interface OrderFilterOptions {
  status?: OrderStatus
  subStatus?: OrderSubStatus
  branchId?: string
  salesmanId?: string
  customerId?: string
  dateFrom?: Date
  dateTo?: Date
  searchQuery?: string
  limit?: number
}

// Order Pagination
export interface OrderPagination {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

// Order List Response
export interface OrderListResponse {
  orders: Order[]
  pagination: OrderPagination
  statistics: OrderStatistics
} 
